<script>
  import { format } from 'svelte-i18n';
  import { Badge, Button, Card, CardBody } from '@sveltestrap/sveltestrap';
  import { format as formatDate } from 'date-fns';

  import {
    DirectoryFields,
    KeyValueField,
    ListField,
    PasswordField,
    SelectField,
    SwitchField,
    TextareaField,
    TextField
  } from '@client/components/fields/standalone';
  import { ScrollableSection } from '@client/components/scrollspy';

  import { disabledValue, enabledValue } from '@client/pages/manage/domain-settings';

  import { DomainService } from '@client/services/domain';

  import { userStore } from '@client/stores/user';

  import { downloadFile } from '@client/utils/download';

  import {
    AUTO_SYNC_OPTIONS,
    SYNC_CONTENT_TYPES,
    SYNC_HTTP_METHODS,
    SYNC_SFTP_TYPES,
    VCARD_FIELDS_DEFAULTS
  } from '@client/enums';

  import { formatBytes } from '@common/utils/numbers';

  export let settings = {};
  export let onUpdate = null;

  const EXPORT_DATE_FORMAT = 'M-dd-yyyy-hh-mm-ss';
  const EXPORT_TYPE = 'text/csv;charset=utf-8';

  let fetchingFolder = false;
  let downloadingFile = false;
  let editingFieldingMappings = false;
  let folders = [];

  const getFtpFile = async () => {
    downloadingFile = true;

    const response = await DomainService.getFtpFile();

    downloadingFile = false;

    if (response && !response?.status) {
      const blob = new Blob([response], { type: EXPORT_TYPE });
      const today = formatDate(new Date(), EXPORT_DATE_FORMAT);

      downloadFile(blob, `downloaded-ftp-${today}.csv`);
    }

    return response;
  };

  const getFtpFolder = async () => {
    folders = [];
    fetchingFolder = true;

    const response = await DomainService.getFtpFolder();

    fetchingFolder = false;

    if (response && !response?.status) {
      folders = response.sort((a, b) => new Date(b.modifyTime) - new Date(a.modifyTime));
    }

    return folders;
  };

  let revealing = false;
  let fetchedPassword = null;

  const getSftpPassword = async () => {
    if (revealing) {
      if (fetchedPassword) {
        settings.sftpserverpassword = fetchedPassword;
        settings = { ...settings };
        return;
      }

      const response = await DomainService.getSftpPassword();

      if (response && !response?.status) {
        fetchedPassword = response;
        settings.sftpserverpassword = response;
        settings = { ...settings };
      }
    }
  };
</script>

<div id="directory">
  <ScrollableSection id="directory-general">
    <h1 class="mb-3">{$format('label.GENERAL')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="enablefieldmappings"
          label={$format('label.FIELD_MAPPINGS')}
          tooltip={$format('label.TOOLTIP.FIELD_MAPPINGS')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:editing={editingFieldingMappings}
          bind:checked={settings.enablefieldmappings}
          {onUpdate}
        />

        <DirectoryFields
          id="fieldmappings"
          label={$format('label.CONFIGURE_FIELD_MAPPINGS', {
            values: { enabled: settings.enablefieldmappings ? $format('label.LIVE') : $format('label.DRAFT') }
          })}
          tooltip={$format('label.TOOLTIP.CONFIGURE_FIELD_MAPPINGS')}
          bind:fields={settings.fieldmappings}
          {onUpdate}
        />

        <SwitchField
          id="enablevcards"
          label={$format('label.ENABLE_VCARDS')}
          tooltip={$format('label.TOOLTIP.ENABLE_VCARDS')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.enablevcards}
          {onUpdate}
        />

        <KeyValueField
          id="vcardfieldmappings"
          label={$format('label.VCARD_FIELD_MAPPINGS')}
          tooltip={$format('label.TOOLTIP.VCARD_FIELD_MAPPINGS')}
          addButtonLabel={$format('label.ADD_FIELD')}
          keyColumnLabel={$format('label.FIELD')}
          keyProperty="field"
          valueColumnLabel={$format('label.VALUE')}
          bind:values={settings.vcardfieldmappings}
          {onUpdate}
        >
          <span slot="action" class="field-mapping-actions" let:onExternalAdd>
            <Button size="sm" color="secondary" on:click={() => onExternalAdd([...VCARD_FIELDS_DEFAULTS])}>
              {$format('label.COPY_DEFAULT_VCARD_FIELDS')}
            </Button>
          </span>
        </KeyValueField>

        <SwitchField
          id="blankoverrideunlocked"
          label={$format('label.BLANK_UNLOCKED_FIELDS_OVERRIDE')}
          tooltip={$format('label.TOOLTIP.BLANK_UNLOCKED_FIELDS_OVERRIDE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.blankoverrideunlocked}
          {onUpdate}
        />

        <SelectField
          id="defaultsyncoptions-replace"
          label={$format('label.DEFAULT_AUTO_SYNC_BEHAVIOR')}
          tooltip={$format('label.TOOLTIP.DEFAULT_AUTO_SYNC_BEHAVIOR')}
          selected={AUTO_SYNC_OPTIONS.find(({ value }) => value === settings.defaultsyncoptions.replace)}
          options={AUTO_SYNC_OPTIONS}
          bind:value={settings.defaultsyncoptions.replace}
          {onUpdate}
        />

        <SwitchField
          id="csvtree-enabled"
          label={$format('label.CSV_HIERARCHY_SYNCING')}
          tooltip={$format('label.TOOLTIP.CSV_HIERARCHY_SYNCING')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.csvtree.enabled}
          {onUpdate}
        />

        <TextField
          id="csvtree-label"
          label={$format('label.CSV_SYSTEM_NAME')}
          tooltip=""
          bind:value={settings.csvtree.label}
          {onUpdate}
        />

        <TextField
          id="csvtree-rootid"
          label={$format('label.CSV_SYSTEM_ROOT_ID')}
          tooltip={$format('label.TOOLTIP.CSV_SYSTEM_ROOT_ID')}
          bind:value={settings.csvtree.rootid}
          {onUpdate}
        />

        <SwitchField
          id="custompurge-enabled"
          label={$format('label.ENABLE_CUSTOM_PURGE')}
          tooltip={$format('label.TOOLTIP.ENABLE_CUSTOM_PURGE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.custompurge.enabled}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  {#if settings.office365enabled || $userStore.isStaff}
    <ScrollableSection id="directory-microsoft">
      <h1 class="mb-3">{$format('label.MICROSOFT_365')}</h1>

      <Card>
        <CardBody class="p-4 pt-1">
          <TextField
            id="o365admin"
            label={$format('label.ADMIN_EMAIL')}
            tooltip={$format('label.TOOLTIP.ADMIN_EMAIL_MICROSOFT')}
            bind:value={settings.o365admin}
            {onUpdate}
            allowCopy
          />

          <SwitchField
            id="autosyncoffice365users"
            label={$format('label.AUTO_SYNC_USERS')}
            tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_MICROSOFT')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.autosyncoffice365users}
            {onUpdate}
          />

          <SwitchField
            id="autosyncoffice365groups"
            label={$format('label.AUTO_SYNC_GROUPS')}
            tooltip={$format('label.TOOLTIP.AUTO_SYNC_GROUPS_MICROSOFT')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.autosyncoffice365groups}
            {onUpdate}
          />

          <SwitchField
            id="enableo365searchfields"
            label={$format('label.CONTACT_FIELDS_SEARCH')}
            tooltip={$format('label.TOOLTIP.CONTACT_FIELDS_SEARCH')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enableo365searchfields}
            {onUpdate}
          />

          <SwitchField
            id="enableo365photos"
            label={$format('label.PHOTO_THUMBNAILS')}
            tooltip={$format('label.TOOLTIP.PHOTO_THUMBNAILS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enableo365photos}
            {onUpdate}
          />

          <SwitchField
            id="o365syncaliases"
            label={$format('label.SYNC_ALIASES')}
            tooltip={$format('label.TOOLTIP.SYNC_ALIASES')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.o365syncaliases}
            {onUpdate}
          />

          <SwitchField
            id="o365syncmanager"
            label={$format('label.SYNC_MANAGERS')}
            tooltip={$format('label.TOOLTIP.SYNC_MANAGERS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.o365syncmanager}
            {onUpdate}
          />

          <SwitchField
            id="o365odatafilterenabled"
            label={$format('label.USER_ODATA_FILTER')}
            tooltip={$format('label.TOOLTIP.USER_ODATA_FILTER')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.o365odatafilterenabled}
            {onUpdate}
          >
            {#if settings.o365odatafilterenabled}
              <ListField
                id="defaultsyncoptions-o365odatafilters"
                label={$format('label.FILTER_VALUES')}
                tooltip={$format('label.TOOLTIP.FILTER_VALUES')}
                addButtonLabel="Add Filter"
                bind:values={settings.defaultsyncoptions.o365odatafilters}
                {onUpdate}
              />
            {/if}
          </SwitchField>

          <SwitchField
            id="o365clientfilterenabled"
            label={$format('label.USER_CLIENT_FILTER')}
            tooltip={$format('label.TOOLTIP.USER_CLIENT_FILTER')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.o365clientfilterenabled}
            {onUpdate}
          >
            {#if settings.o365clientfilterenabled}
              <TextField id="o365clientfilter" label="Filter Value" bind:value={settings.o365clientfilter} {onUpdate} />
            {/if}
          </SwitchField>

          <SwitchField
            id="o365groupodatafilterenabled"
            label={$format('label.GROUP_ODATA_FILTER')}
            tooltip={$format('label.TOOLTIP.GROUP_ODATA_FILTER')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.o365groupodatafilterenabled}
            {onUpdate}
          >
            {#if settings.o365groupodatafilterenabled}
              <TextField
                id="o365groupodatafilter"
                label={$format('label.FILTER_VALUE')}
                bind:value={settings.o365groupodatafilter}
                {onUpdate}
              />
            {/if}
          </SwitchField>

          <SwitchField
            id="o365groupclientfilterenabled"
            label={$format('label.GROUP_FILTER')}
            tooltip={$format('label.TOOLTIP.GROUP_FILTER')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.o365groupclientfilterenabled}
            {onUpdate}
          >
            {#if settings.o365groupclientfilterenabled}
              <TextField
                id="o365groupclientfilter"
                label={$format('label.FILTER_VALUE')}
                bind:value={settings.o365groupclientfilter}
                {onUpdate}
              />
            {/if}
          </SwitchField>
        </CardBody>
      </Card>
    </ScrollableSection>
  {/if}

  {#if settings.googleenabled || $userStore.isStaff}
    <ScrollableSection id="directory-google">
      <h1 class="mb-3">{$format('label.GOOGLE_WORKSPACE')}</h1>

      <Card>
        <CardBody class="p-4 pt-1">
          <TextField
            id="gappsadmin"
            label={$format('label.ADMIN_EMAIL')}
            tooltip={$format('label.TOOLTIP.ADMIN_EMAIL_GOOGLE')}
            bind:value={settings.gappsadmin}
            {onUpdate}
            allowCopy
          />

          <SwitchField
            id="autosyncgoogleusers"
            label={$format('label.AUTO_SYNC_USERS')}
            tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_GOOGLE')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.autosyncgoogleusers}
            {onUpdate}
          />

          <SwitchField
            id="autosyncgooglegroups"
            label={$format('label.AUTO_SYNC_GROUPS')}
            tooltip={$format('label.TOOLTIP.AUTO_SYNC_GROUPS_GOOGLE')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.autosyncgooglegroups}
            {onUpdate}
          />

          <SwitchField
            id="autosyncgoogleorgs"
            label={$format('label.AUTO_SYNC_ORGS')}
            tooltip={$format('label.TOOLTIP.AUTO_SYNC_ORGS')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.autosyncgoogleorgs}
            {onUpdate}
          />

          <SwitchField
            id="enablegappsphotos"
            label={$format('label.PHOTO_THUMBNAILS')}
            tooltip={$format('label.TOOLTIP.PHOTO_THUMBNAILS_GOOGLE')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.enablegappsphotos}
            {onUpdate}
          />

          <SwitchField
            id="gappshighresphoto"
            label={$format('label.HIGH_RESOLUTION_THUMBNAILS_GOOGLE')}
            tooltip={$format('label.TOOLTIP.HIGH_RESOLUTION_THUMBNAILS_GOOGLE')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.gappshighresphoto}
            {onUpdate}
          />

          <SwitchField
            id="gappssyncaliases"
            label={$format('label.SYNC_ALIASES')}
            tooltip={$format('label.TOOLTIP.SYNC_ALIASES')}
            valueOn={$format(enabledValue)}
            valueOff={$format(disabledValue)}
            bind:checked={settings.gappssyncaliases}
            {onUpdate}
          />
        </CardBody>
      </Card>
    </ScrollableSection>
  {/if}

  <ScrollableSection id="directory-custom">
    <h1 class="mb-3">{$format('label.CUSTOM_DIRECTORY')}</h1>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="customdirauthkey"
          label={$format('label.API_KEY')}
          tooltip={$format('label.TOOLTIP.API_KEY_CUSTOM_DIRECTORY')}
          bind:value={settings.customdirauthkey}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="customdirendpoint"
          label={$format('label.API_ENDPOINT')}
          tooltip={$format('label.TOOLTIP.API_ENDPOINT_CUSTOM_DIRECTORY')}
          bind:value={settings.customdirendpoint}
          {onUpdate}
          allowCopy
        />

        <SelectField
          id="customdirendpointmethod"
          label={$format('label.API_HTTP_METHOD')}
          tooltip={$format('label.TOOLTIP.API_HTTP_METHOD')}
          selected={SYNC_HTTP_METHODS.find(({ value }) => value === (settings.customdirendpointmethod || '-'))}
          options={SYNC_HTTP_METHODS}
          bind:value={settings.customdirendpointmethod}
          {onUpdate}
        />

        <SelectField
          id="customdirendpointpostcontenttype"
          label={$format('label.API_POST_CONTENT_TYPE')}
          tooltip={$format('label.TOOLTIP.API_POST_CONTENT_TYPE')}
          selected={SYNC_CONTENT_TYPES.find(
            ({ value }) => value === (settings.customdirendpointpostcontenttype || 'application/json')
          )}
          options={SYNC_CONTENT_TYPES}
          bind:value={settings.customdirendpointpostcontenttype}
          {onUpdate}
        />

        <TextareaField
          id="customdirendpointpostbody"
          label={$format('label.API_POST_BODY')}
          tooltip={$format('label.TOOLTIP.API_POST_BODY')}
          bind:value={settings.customdirendpointpostbody}
          {onUpdate}
        />

        <TextField
          id="customdirtokenendpoint"
          label={$format('label.TOKEN_API_ENDPOINT')}
          tooltip={$format('label.TOOLTIP.TOKEN_API_ENDPOINT')}
          bind:value={settings.customdirtokenendpoint}
          {onUpdate}
        />

        <SelectField
          id="customdirtokenmethod"
          label={$format('label.TOKEN_HTTP_METHOD')}
          tooltip={$format('label.TOOLTIP.TOKEN_HTTP_METHOD')}
          selected={SYNC_HTTP_METHODS.find(({ value }) => value === (settings.customdirtokenmethod || '-'))}
          options={SYNC_HTTP_METHODS}
          bind:value={settings.customdirtokenmethod}
          {onUpdate}
        />

        <SelectField
          id="customdirtokenpostcontenttype"
          label={$format('label.TOKEN_POST_CONTENT_TYPE')}
          tooltip={$format('label.TOOLTIP.TOKEN_POST_CONTENT_TYPE')}
          selected={SYNC_CONTENT_TYPES.find(
            ({ value }) => value === (settings.customdirtokenpostcontenttype || 'application/json')
          )}
          options={SYNC_CONTENT_TYPES}
          bind:value={settings.customdirtokenpostcontenttype}
          {onUpdate}
        />

        <TextareaField
          id="customdirtokenpostbody"
          label={$format('label.TOKEN_POST_BODY')}
          tooltip={$format('label.TOOLTIP.TOKEN_POST_BODY')}
          bind:value={settings.customdirtokenpostbody}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="customdirtokenfieldname"
          label={$format('label.TOKEN_FIELD_NAME')}
          tooltip={$format('label.TOOLTIP.TOKEN_FIELD_NAME')}
          bind:value={settings.customdirtokenfieldname}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="customdirformat"
          label={$format('label.FORMAT')}
          tooltip={$format('label.TOOLTIP.FORMAT')}
          bind:value={settings.customdirformat}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="customdirresultsarrayfield"
          label={$format('label.RESULT_ARRAY_FIELD_NAME')}
          tooltip={$format('label.TOOLTIP.RESULT_ARRAY_FIELD_NAME')}
          bind:value={settings.customdirresultsarrayfield}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="customdiremailfield"
          label={$format('label.EMAIL_FIELD')}
          tooltip={$format('label.TOOLTIP.EMAIL_FIELD')}
          bind:value={settings.customdiremailfield}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="customdirchangefield"
          label={$format('label.CHANGE_FINGERPRINT_FIELD')}
          tooltip={$format('label.TOOLTIP.CHANGE_FINGERPRINT_FIELD')}
          bind:value={settings.customdirchangefield}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="customdirauthheader"
          label={$format('label.AUTHENTICATION_HEADER')}
          tooltip={$format('label.TOOLTIP.AUTHENTICATION_HEADER')}
          bind:value={settings.customdirauthheader}
          {onUpdate}
          allowCopy
        />

        <SwitchField
          id="autosynccustomdirusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_CUSTOM')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosynccustomdirusers}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="directory-sftp">
    <h1 class="mb-3">SFTP</h1>
    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="enablesftpserver"
          label={$format('label.SFTP_OPENSENSE_SERVER')}
          tooltip={$format('label.TOOLTIP.SFTP_OPENSENSE_SERVER', { values: { domainname: settings.domainname } })}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.enablesftpserver}
          {onUpdate}
        />
        {#if settings.enablesftpserver}
          <PasswordField
            id="sftpserverpassword"
            label={$format('label.SFTP_OPENSENSE_SERVER_PASSWORD')}
            tooltip={$format('label.TOOLTIP.SFTP_OPENSENSE_SERVER_PASSWORD')}
            bind:value={settings.sftpserverpassword}
            bind:revealing
            reveal
            onReveal={getSftpPassword}
            {onUpdate}
            allowCopy={revealing}
          />

          <TextField
            id="sftpserverpublickey"
            label={$format('label.SFTP_PUBLIC_KEY_OPTIONAL')}
            tooltip={$format('label.TOOLTIP.SFTP_PUBLIC_KEY_OPTIONAL', { values: { domainname: settings.domainname } })}
            bind:value={settings.sftpserverpublickey}
            {onUpdate}
            allowCopy
          />
        {/if}

        <SelectField
          id="ftpusersyncprotocol"
          label={$format('label.SFTP_DEFAULT_PROTOCOL')}
          tooltip={$format('label.TOOLTIP.SFTP_DEFAULT_PROTOCOL')}
          selected={SYNC_SFTP_TYPES.find(({ value }) => value === (settings.ftpusersyncprotocol || 'sftp'))}
          options={SYNC_SFTP_TYPES}
          bind:value={settings.ftpusersyncprotocol}
          {onUpdate}
        />

        <TextField
          id="ftpusersynchost"
          label={$format('label.SFTP_HOSTNAME')}
          tooltip={$format('label.TOOLTIP.SFTP_HOSTNAME')}
          bind:value={settings.ftpusersynchost}
          {onUpdate}
        />

        <TextField
          id="ftpusersyncusername"
          label={$format('label.SFTP_USER')}
          tooltip={$format('label.TOOLTIP.SFTP_USER')}
          bind:value={settings.ftpusersyncusername}
          {onUpdate}
        />

        <PasswordField
          id="ftpusersyncpassword"
          label={$format('label.SFTP_PASSWORD')}
          tooltip={$format('label.TOOLTIP.SFTP_PASSWORD')}
          bind:value={settings.ftpusersyncpassword}
          {onUpdate}
        />

        <TextField
          id="ftpusersyncrootdir"
          label={$format('label.SFTP_ROOT_FOLDER')}
          tooltip={$format('label.TOOLTIP.SFTP_ROOT_FOLDER')}
          bind:value={settings.ftpusersyncrootdir}
          {onUpdate}
        >
          <Button class="my-2" color="secondary" size="sm" on:click={getFtpFolder} disabled={fetchingFolder}>
            {#if fetchingFolder}
              {$format('label.LOADING')}...
            {:else}
              {$format('label.SHOW_FOLDER_CONTENTS')}
            {/if}
          </Button>

          {#if folders.length}
            <div class="ftp-folders">
              <div class="ftp-folder headings">
                <div>{$format('label.FILENAME')}</div>
                <div>{$format('label.TIMESTAMP')}</div>
              </div>
              {#each folders as folder, index}
                <div class="ftp-folder">
                  <div>{folder.name} <small>({formatBytes(folder.size)})</small></div>
                  <div>
                    {formatDate(folder.modifyTime, 'MM/dd/yyyy @ hh:mm:ss aa')}
                    {#if index === 0}
                      <Badge color="warning" class="mx-2">{$format('label.LATEST')}</Badge>
                    {/if}
                  </div>
                </div>
              {/each}
            </div>
          {/if}
        </TextField>

        <TextField
          id="ftpusersyncfilter"
          label={$format('label.SFTP_FILENAME_FILTER')}
          tooltip={$format('label.TOOLTIP.SFTP_FILENAME_FILTER')}
          bind:value={settings.ftpusersyncfilter}
          {onUpdate}
        >
          <Button class="my-2" color="secondary" size="sm" on:click={getFtpFile} disabled={downloadingFile}>
            {#if downloadingFile}
              {$format('label.LOADING')}...
            {:else}
              {$format('label.DOWNLOAD')}
            {/if}
          </Button>
        </TextField>

        <SwitchField
          id="ftpusersyncencrypted"
          label={$format('label.SFTP_ENCRYPTED_FILE')}
          tooltip={$format('label.TOOLTIP.SFTP_ENCRYPTED_FILE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.ftpusersyncencrypted}
          {onUpdate}
        />

        <TextField
          id="ftpusersyncemailfield"
          label={$format('label.SFTP_EMAIL_FIELD')}
          tooltip={$format('label.TOOLTIP.SFTP_EMAIL_FIELD')}
          bind:value={settings.ftpusersyncemailfield}
          {onUpdate}
        />

        <SwitchField
          id="ftpusersyncenabled"
          label={$format('label.SFTP_USER_SYNCING')}
          tooltip={$format('label.TOOLTIP.SFTP_USER_SYNCING')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.ftpusersyncenabled}
          {onUpdate}
        />

        <SwitchField
          id="autosyncftpusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_FTP')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncftpusers}
          {onUpdate}
        />

        <h2 class="mb-3">{$format('label.CSV_TREE')}</h2>

        <SelectField
          id="ftpcsvtreesyncprotocol"
          label={$format('label.SFTP_DEFAULT_PROTOCOL')}
          tooltip={$format('label.TOOLTIP.SFTP_DEFAULT_PROTOCOL')}
          selected={SYNC_SFTP_TYPES.find(({ value }) => value === (settings.ftpcsvtreesyncprotocol || 'sftp'))}
          options={SYNC_SFTP_TYPES}
          bind:value={settings.ftpcsvtreesyncprotocol}
          {onUpdate}
        />

        <TextField
          id="ftpcsvtreesynchost"
          label={$format('label.SFTP_HOSTNAME')}
          tooltip={$format('label.TOOLTIP.SFTP_HOSTNAME')}
          bind:value={settings.ftpcsvtreesynchost}
          {onUpdate}
        />

        <TextField
          id="ftpcsvtreesyncusername"
          label={$format('label.SFTP_USER')}
          tooltip={$format('label.TOOLTIP.SFTP_USER')}
          bind:value={settings.ftpcsvtreesyncusername}
          {onUpdate}
        />

        <PasswordField
          id="ftpcsvtreesyncpassword"
          label={$format('label.SFTP_PASSWORD')}
          tooltip={$format('label.TOOLTIP.SFTP_PASSWORD')}
          bind:value={settings.ftpcsvtreesyncpassword}
          {onUpdate}
        />

        <TextField
          id="ftpcsvtreesyncrootdir"
          label={$format('label.SFTP_ROOT_FOLDER')}
          tooltip={$format('label.TOOLTIP.SFTP_ROOT_FOLDER')}
          bind:value={settings.ftpcsvtreesyncrootdir}
          {onUpdate}
        />

        <TextField
          id="ftpcsvtreesyncfilter"
          label={$format('label.SFTP_FILENAME_FILTER')}
          tooltip={$format('label.TOOLTIP.SFTP_FILENAME_FILTER')}
          bind:value={settings.ftpcsvtreesyncfilter}
          {onUpdate}
        />

        <SwitchField
          id="ftpcsvtreesyncencrypted"
          label={$format('label.SFTP_ENCRYPTED_FILE')}
          tooltip={$format('label.TOOLTIP.SFTP_ENCRYPTED_FILE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.ftpcsvtreesyncencrypted}
          {onUpdate}
        />

        <SwitchField
          id="ftpcsvtreesyncenabled"
          label={$format('label.SFTP_HIERARCHY_SYNCING')}
          tooltip={$format('label.TOOLTIP.SFTP_HIERARCHY_SYNCING')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.ftpcsvtreesyncenabled}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>

  <ScrollableSection id="directory-configurations">
    <h1 class="mb-3">{$format('label.CONFIGURATIONS')}</h1>

    <h2 class="mb-3">{$format('label.AIRTABLE')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="airtablebaseid"
          label={$format('label.AIRTABLE_BASE_ID')}
          tooltip={$format('label.TOOLTIP.BASE_ID_AIRTABLE')}
          bind:value={settings.airtablebaseid}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="airtabletablename"
          label={$format('label.AIRTABLE_TABLE')}
          tooltip={$format('label.TOOLTIP.TABLENAME_AIRTABLE')}
          bind:value={settings.airtabletablename}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="airtabletoken"
          label={$format('label.API_TOKEN')}
          tooltip={$format('label.TOOLTIP.API_TOKEN_AIRTABLE')}
          bind:value={settings.airtabletoken}
          {onUpdate}
        />

        <TextField
          id="airtableemailfield"
          label={$format('label.EMAIL_FIELD')}
          tooltip={$format('label.TOOLTIP.EMAIL_FIELD_AIRTABLE')}
          bind:value={settings.airtableemailfield}
          {onUpdate}
          allowCopy
        />

        <SwitchField
          id="autosyncairtableusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_AIRTABLE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncairtableusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">BambooHR</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="bamboohrtoken"
          label={$format('label.API_TOKEN')}
          tooltip={$format('label.TOOLTIP.API_TOKEN_BAMBOOHR')}
          bind:value={settings.bamboohrtoken}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="bamboohrsubdomain"
          label={$format('label.SUBDOMAIN')}
          tooltip={$format('label.TOOLTIP.SUBDOMAIN_BAMBOOHR')}
          bind:value={settings.bamboohrsubdomain}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="bamboohremailfield"
          label={$format('label.EMAIL_FIELD')}
          tooltip={$format('label.TOOLTIP.EMAIL_FIELD_BAMBOO')}
          bind:value={settings.bamboohremailfield}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="bamboohrfields"
          label={$format('label.FIELD_LIST')}
          tooltip={$format('label.TOOLTIP.FIELD_LIST_BAMBOO')}
          bind:value={settings.bamboohrfields}
          {onUpdate}
          allowCopy
        />

        <SwitchField
          id="autosyncbamboohrusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_BAMBOOHR')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncbamboohrusers}
          {onUpdate}
        />

        <SwitchField
          id="enablebamboohrphotos"
          label={$format('label.PHOTO_THUMBNAILS')}
          tooltip={$format('label.TOOLTIP.PHOTO_THUMBNAILS_BAMBOOHR')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.enablebamboohrphotos}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.CALENDLY')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="autosynccalendlyusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_CALENDLY')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosynccalendlyusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.FRONTAPP')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="frontapptoken"
          label={$format('label.API_TOKEN')}
          tooltip={$format('label.TOOLTIP.API_TOKEN_FRONTAPP')}
          bind:value={settings.frontapptoken}
          {onUpdate}
        />

        <SwitchField
          id="autosyncfrontappusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_FRONT')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncfrontappusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.JUMPCLOUD')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="autosyncjumpcloudusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_JUMPCLOUD')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncjumpcloudusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.MANGOAPPS')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <PasswordField
          id="mangoappsapikey"
          label={$format('label.API_TOKEN')}
          tooltip={$format('label.TOOLTIP.API_TOKEN_MANGOAPPS')}
          bind:value={settings.mangoappsapikey}
          {onUpdate}
        />

        <TextField
          id="mangoappsusername"
          label={$format('label.USERNAME')}
          tooltip={$format('label.TOOLTIP.USERNAME_MANGOAPPS')}
          bind:value={settings.mangoappsusername}
          {onUpdate}
          allowCopy
        />

        <PasswordField
          id="mangoappspassword"
          label={$format('label.PASSWORD')}
          tooltip={$format('label.TOOLTIP.PASSWORD_MANGOAPPS')}
          bind:value={settings.mangoappspassword}
          {onUpdate}
        />

        <TextField
          id="mangoappsemailfield"
          label={$format('label.EMAIL_FIELD')}
          tooltip={$format('label.TOOLTIP.EMAIL_FIELD')}
          bind:value={settings.mangoappsemailfield}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.OKTA')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="oktasubdomain"
          label={$format('label.OKTA_DIRECTORY_API_ENDPOINT')}
          tooltip={$format('label.TOOLTIP.OKTA_DIRECTORY_API_ENDPOINT')}
          bind:value={settings.oktasubdomain}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="oktatoken"
          label={$format('label.OKTA_DIRECTORY_API_TOKEN')}
          tooltip={$format('label.TOOLTIP.OKTA_DIRECTORY_API_TOKEN')}
          bind:value={settings.oktatoken}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="oktaemailfield"
          label={$format('label.OKTA_EMAIL_ATTRIBUTE')}
          tooltip={$format('label.TOOLTIP.OKTA_EMAIL_ATTRIBUTE')}
          bind:value={settings.oktaemailfield}
          formatValue={(val) => (val?.length ? val : 'email')}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="oktasearch"
          label={$format('label.OKTA_USER_SEARCH_FILTER')}
          tooltip={$format('label.TOOLTIP.OKTA_USER_SEARCH_FILTER')}
          bind:value={settings.oktasearch}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="oktavalidstatuses"
          label={$format('label.VALID_OKTA_USER_STATUSES')}
          tooltip={$format('label.TOOLTIP.VALID_OKTA_USER_STATUSES')}
          bind:value={settings.oktavalidstatuses}
          {onUpdate}
          allowCopy
        />

        <SwitchField
          id="autosyncoktagroups"
          label={$format('label.AUTO_SYNC_GROUPS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_GROUPS_OKTA')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncoktagroups}
          {onUpdate}
        />

        <SwitchField
          id="autosyncoktausers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_OKTA')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncoktausers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.ONELOGIN')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="oneloginclientid"
          label={$format('label.CLIENT_ID')}
          tooltip={$format('label.TOOLTIP.CLIENT_ID_ONELOGIN')}
          bind:value={settings.oneloginclientid}
          {onUpdate}
          allowCopy
        />

        <PasswordField
          id="oneloginclientsecret"
          label={$format('label.CLIENT_SECRET')}
          tooltip={$format('label.TOOLTIP.CLIENT_SECRET_ONELOGIN')}
          bind:value={settings.oneloginclientsecret}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="oneloginsubdomain"
          label={$format('label.SUBDOMAIN')}
          tooltip={$format('label.TOOLTIP.SUBDOMAIN_ONELOGIN')}
          bind:value={settings.oneloginsubdomain}
          {onUpdate}
          allowCopy
        />

        <SwitchField
          id="autosynconeloginusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_ONELOGIN')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosynconeloginusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.OUTREACH')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="autosyncoutreachusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_OUTREACH')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncoutreachusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">Paylocity</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="paylocitycompanyid"
          label={$format('label.COMPANY_ID')}
          tooltip={$format('label.TOOLTIP.COMPANY_ID_PAYLOCITY')}
          bind:value={settings.paylocitycompanyid}
          {onUpdate}
          allowCopy
        />

        <TextField
          id="paylocityclientid"
          label={$format('label.CLIENT_ID')}
          tooltip={$format('label.TOOLTIP.CLIENT_ID_PAYLOCITY')}
          bind:value={settings.paylocityclientid}
          {onUpdate}
          allowCopy
        />

        <PasswordField
          id="paylocityclientsecret"
          label={$format('label.CLIENT_SECRET')}
          tooltip={$format('label.TOOLTIP.CLIENT_SECRET_PAYLOCITY')}
          bind:value={settings.paylocityclientsecret}
          {onUpdate}
          allowCopy
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.PINGBOARD')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <TextField
          id="pingboardclientid"
          label={$format('label.CLIENT_ID')}
          tooltip={$format('label.TOOLTIP.CLIENT_ID_PINGBOARD')}
          bind:value={settings.pingboardclientid}
          {onUpdate}
          allowCopy
        />

        <PasswordField
          id="pingboardclientsecret"
          label={$format('label.CLIENT_SECRET')}
          tooltip={$format('label.TOOLTIP.CLIENT_SECRET_PINGBOARD')}
          bind:value={settings.pingboardclientsecret}
          {onUpdate}
          allowCopy
        />

        <SwitchField
          id="autosyncpingboardusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_PINGBOARD')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncpingboardusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.SALESFORCE')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="autosyncsalesforceusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_SALESFORCE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncsalesforceusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.SUREFIRE_CRM')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="autosyncsurefireusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_SUREFIRE')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosyncsurefireusers}
          {onUpdate}
        />
      </CardBody>
    </Card>

    <h2 class="mb-3">{$format('label.TOTAL_EXPERT')}</h2>

    <Card>
      <CardBody class="p-4 pt-1">
        <SwitchField
          id="autosynctotalexpertusers"
          label={$format('label.AUTO_SYNC_USERS')}
          tooltip={$format('label.TOOLTIP.AUTO_SYNC_USERS_TOTALEXPERT')}
          valueOn={$format(enabledValue)}
          valueOff={$format(disabledValue)}
          bind:checked={settings.autosynctotalexpertusers}
          {onUpdate}
        />
      </CardBody>
    </Card>
  </ScrollableSection>
</div>

<style lang="scss" src="./directory.scss"></style>
